// Drag-reorder of a post's media (Scott, 2026-09-30). Pure so the ordering is
// asserted without jsdom. `media` is already an ordered PostMedia[] and the
// carousel publishes in that order, so reordering is just a move within the
// array the picker was handed.
//
// ⚠️ The composer has SEVERAL independent media arrays — the base media and
// each destination's media override (plus the grouped story block). Every
// MediaPicker instance reorders ONLY the array it was given and writes it back
// through its own onChange; nothing here knows which array it is, which is
// what keeps a reorder of Facebook's override from touching the base media.
//
// Sortable ids must be STABLE across a reorder (an index-based id would
// re-key every item the moment one moves), and unique even if the same file
// was added twice — so an id is the URL plus its occurrence number.

import type { PostMedia } from '../../types/publishing';

export function mediaSortIds(media: readonly PostMedia[]): string[] {
  const seen = new Map<string, number>();
  return media.map((m) => {
    const n = seen.get(m.url) ?? 0;
    seen.set(m.url, n + 1);
    return `${m.url}#${n}`;
  });
}

/** Move the item with sort id `fromId` to the position of `toId`. Unknown ids → unchanged. */
export function moveMedia(media: readonly PostMedia[], fromId: string, toId: string): PostMedia[] {
  const ids = mediaSortIds(media);
  const from = ids.indexOf(fromId);
  const to = ids.indexOf(toId);
  if (from < 0 || to < 0 || from === to) return [...media];
  const next = [...media];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved!);
  return next;
}
